// import { toast } from "react-hot-toast";
import { lib } from "crypto-js";
// import i18n from "i18n";

import init, {
  // encrypt,
  // decrypt,
  decapsulate,
  derive_key,
  derive_key_with_salt as derive_key_with_salt_wasm,
  encapsulate,
  generate_key_pair,
  hash_password,
  auth_secret as auth_secret_wasm,
  auth_secret_from_mk as auth_secret_from_mk_wasm,
  kek as kek_wasm,
  kek_from_mk as kek_from_mk_wasm,
} from "@lib/crypto/ow2_crypto/ow2_crypto";

import {
  base64ToUint8Array,
  fileToUint8Array,
  hexToUint8Array,
  stringToUint8Array,
  uint8ArrayToBase64,
  uint8ArrayToHex,
  uint8ArrayToString,
} from "./../dataConversions";
import { FLAG_USE_BASE64_ENCODING } from "./encodingConfig";
// import {
//   getPasswordFromLocalStorage,
//   getPasswordFromSessionStorage,
// } from "./storage";
import { windowCryptoDecrypt, windowCryptoEncrypt } from "./window-crypto";
import { isWebAssemblyAvailable } from "./wasmSupport";
import { captureError } from "@/components/error-boundary/capture";

// Initialize the wasm module.
// Guard the call so we never fire a doomed init() in environments where
// WebAssembly is unavailable or codegen is blocked (#943) — iOS Lockdown Mode,
// enterprise "Island" browser, etc. In those environments the boot guard renders
// a friendly unsupported screen instead of mounting the app.
if (isWebAssemblyAvailable()) {
  void Promise.resolve(init()).catch((err: unknown) => {
    // Reaching here means WebAssembly is present but init() still failed (e.g.
    // a network failure fetching the .wasm, or a corrupted binary). We must not
    // let this surface as an unhandled rejection, but we also must NOT go dark:
    // report it (tagged non-fatal) so a broken-but-WASM-present deploy stays
    // observable in crash telemetry.
    captureError({
      error: err instanceof Error ? err : new Error(String(err)),
      scope: "root",
      source: "suppressed",
    });
  });
}

export const hashPassword = hash_password;

export const generateChatSecretKey = () => {
  const encoder = new TextEncoder();
  const key = lib.WordArray.random(256 / 16).toString();
  return uint8ArrayToHex(encoder.encode(key));
};

// Generate key pair
// @returns {
//    privateKey as hex string,
//    publicKey as hex string
// }
export const generateKeyPair = () => {
  const result = generate_key_pair();
  return {
    privateKeys: uint8ArrayToHex(result.private),
    publicKeys: uint8ArrayToHex(result.public),
  };
};

// Encrypt the private keys using the password
// @privateKeys hex private keys
// @derivedKey hex derived key
// @returns encrypted private keys as hex string

// => DEPRECATED
// export const encryptPrivateKey = (
//   privateKeys: string,
//   derivedKey: string
// ): string => {
//   // Prep data
//   const privateKeysUint8Array = hexToUint8Array(privateKeys);
//   const derivedKeyUint8Array = hexToUint8Array(derivedKey);
//   // Encrypt
//   const result = encrypt(privateKeysUint8Array, derivedKeyUint8Array);
//   // Return hex representation of the result
//   return uint8ArrayToHex(result);
// };

export const encryptPrivateKey = async (
  privateKeys: string,
  derivedKey: string,
): Promise<string> => {
  // Prep data
  const privateKeysUint8Array = hexToUint8Array(privateKeys);
  const derivedKeyUint8Array = hexToUint8Array(derivedKey);
  // Encrypt
  const encryptedBlob = await windowCryptoEncrypt(
    privateKeysUint8Array.buffer,
    derivedKeyUint8Array,
  );

  if (!encryptedBlob) {
    throw new Error("Encryption failed");
  }

  const encryptedArrayBuffer = await encryptedBlob.arrayBuffer();
  const result = new Uint8Array(encryptedArrayBuffer);
  // Return hex representation of the result
  return uint8ArrayToHex(result);
};

// Decrypt the private keys using the password
// @encryptedPrivateKeys hex encrypted private keys
// @returns decrypted private keys as hex string

// => DEPRECATED
// export const decryptPrivateKey = (encryptedPrivateKeys: string): string => {
//   // Get password from session storage
//   let derivedKey = getPasswordFromSessionStorage();
//   if (!derivedKey) {
//     derivedKey = getPasswordFromLocalStorage();
//   }
//   // Check if password exists
//   if (!derivedKey) {
//     throw new Error("No password found in session storage");
//   }
//   // Prep data
//   const encryptedPrivateKeysUint8Array = hexToUint8Array(encryptedPrivateKeys);
//   const keyUint8Array = hexToUint8Array(derivedKey);
//   // Decrypt
//   const result = decrypt(encryptedPrivateKeysUint8Array, keyUint8Array);
//   // Return hex representation of the result
//   return uint8ArrayToHex(result);
// };

export const decryptPrivateKey = async (
  encryptedPrivateKeys: string,
  encryptedPassword: string,
): Promise<string> => {
  // Check if password exists
  if (!encryptedPassword) {
    throw new Error("No password found in session storage");
  }
  // Prep data
  const encryptedPrivateKeysUint8Array = hexToUint8Array(encryptedPrivateKeys);
  const keyUint8Array = hexToUint8Array(encryptedPassword);
  // Decrypt
  const decryptedArrayBuffer = await windowCryptoDecrypt(
    encryptedPrivateKeysUint8Array.buffer,
    keyUint8Array,
  );

  if (!decryptedArrayBuffer) {
    throw new Error("Decryption failed");
  }

  const result = new Uint8Array(decryptedArrayBuffer);
  // Return hex representation of the result
  return uint8ArrayToHex(result);
};

// Encrypt the chat secret key to be sent
// to the server as member secure payload
// @receiverPublicKeys hex receiver public keys
// @senderPrivateKeys hex sender private keys
// @chatSecretKey hex chat secret key
// @returns hex secure payload
export const encapsulateSecurePayload = ({
  receiverPublicKeys,
  senderPrivateKeys,
  chatSecretKey,
}: {
  receiverPublicKeys: string;
  senderPrivateKeys: string;
  chatSecretKey: string;
}): string => {
  // Prep data
  const receiverPublicKeysUint8Array = hexToUint8Array(receiverPublicKeys);
  const senderPrivateKeysUint8Array = hexToUint8Array(senderPrivateKeys);
  const chatSecretKeyUint8Array = hexToUint8Array(chatSecretKey);
  // Encrypt the chat secret key
  const securePayload = encapsulate(
    receiverPublicKeysUint8Array,
    senderPrivateKeysUint8Array,
    chatSecretKeyUint8Array,
  );
  // Return hex string representation of the secure payload
  return uint8ArrayToHex(securePayload);
};

// Decrypt the chat secret key received from the server
// to be used to decrypt the messages
export const decapsulateSecurePayload = ({
  privateKeys,
  securePayload,
}: {
  privateKeys: string;
  securePayload: string;
}): string => {
  // Prep data
  const privateKeysUint8Array = hexToUint8Array(privateKeys);
  const securePayloadUint8Array = hexToUint8Array(securePayload);
  // Decrypt the chat secret key
  const secretKey = decapsulate(privateKeysUint8Array, securePayloadUint8Array);
  // Return string representation of the secret key
  return uint8ArrayToHex(secretKey);
};

// Encrypt
// @plaintext string plain/readable text
// @key hex key
// @returns encoded ciphertext — base64 when `FLAG_USE_BASE64_ENCODING` is true,
//          otherwise legacy hex (with `0x` prefix). Encoding is selected at
//          build time via `src/lib/crypto/encodingConfig.ts`.

// => DEPRECATED
// export const encryptString = (plaintext: string, key: string): string => {
//   // Prep data
//   const plaintextUint8Array = stringToUint8Array(plaintext);
//   const keyUint8Array = hexToUint8Array(key);
//   // Encrypt
//   const result: Uint8Array = encrypt(plaintextUint8Array, keyUint8Array);
//   // Return string representation of the result
//   return uint8ArrayToHex(result);
// };

export const encryptString = async (
  plaintext: string,
  key: string,
): Promise<string> => {
  // Prep data
  const plaintextUint8Array = stringToUint8Array(plaintext);
  const keyUint8Array = hexToUint8Array(key);
  // Encrypt
  const encryptedBlob = await windowCryptoEncrypt(
    plaintextUint8Array.buffer,
    keyUint8Array,
  );

  if (!encryptedBlob) {
    throw new Error("Encryption failed");
  }

  const encryptedArrayBuffer = await encryptedBlob.arrayBuffer();
  const result = new Uint8Array(encryptedArrayBuffer);
  return FLAG_USE_BASE64_ENCODING
    ? uint8ArrayToBase64(result)
    : uint8ArrayToHex(result);
};

// Decrypt
// @ciphertext hex ciphertext
// @key hex key
// @returns plain/readable text

// => DEPRECATED
// export const decryptString = (ciphertext: string, key: string): string => {
//   const ciphertextUint8Array = hexToUint8Array(ciphertext);
//   const keyUint8Array = hexToUint8Array(key);
//   // Decrypt
//   const result: Uint8Array = decrypt(ciphertextUint8Array, keyUint8Array);
//   // Return string representation of the result
//   return uint8ArrayToString(result);
// };

/**
 * Checks whether a string looks like a legacy hex-encoded ciphertext.
 * Requires the "0x" prefix, even total length, and only hex digits after the prefix.
 */
export function isHexCiphertext(text: string): boolean {
  return (
    text.startsWith("0x") &&
    text.length % 2 === 0 &&
    /^0x[0-9a-fA-F]+$/.test(text)
  );
}

export const decryptString = async (
  ciphertext: string,
  key: string,
): Promise<string> => {
  // Support both legacy hex-encoded ("0x…") and current base64-encoded ciphertexts
  const ciphertextUint8Array = isHexCiphertext(ciphertext)
    ? hexToUint8Array(ciphertext)
    : base64ToUint8Array(ciphertext);
  const keyUint8Array = hexToUint8Array(key);
  // Decrypt
  const decryptedArrayBuffer = await windowCryptoDecrypt(
    ciphertextUint8Array.buffer,
    keyUint8Array,
  );

  if (!decryptedArrayBuffer) {
    throw new Error("Decryption failed");
  }

  const result = new Uint8Array(decryptedArrayBuffer);
  // Return string representation of the result
  return uint8ArrayToString(result);
};

// Generate derived key from password and username
// @password string
// @username string
// @returns hex derived key
export const deriveKey = (password: string, username: string): string => {
  // Generate derived key
  const result = derive_key(password, username);
  // Return string representation of the result
  return uint8ArrayToHex(result);
};

// v3 master-key derivation with an explicit per-user random salt (hex) instead
// of the username. Returns hex, matching deriveKey.
// @saltHex hex-encoded random salt (16 bytes for v3)
export const deriveKeyWithSalt = (
  password: string,
  saltHex: string,
): string => {
  // Argon2id requires a salt of at least 8 bytes (argon2::MIN_SALT_LEN). The
  // WASM `hash_password_into(...).expect(...)` PANICS on a shorter salt, which
  // aborts the module and crashes the app (this is the "unreachable" failure a
  // malformed/empty kdf_salt produced). Guard here and throw a normal Error so
  // callers (login, the password-check .refine flows) can handle it gracefully.
  const saltBytes = hexToUint8Array(saltHex);
  if (saltBytes.byteLength < 8) {
    throw new Error("Invalid KDF salt: must be at least 8 bytes");
  }
  const result = derive_key_with_salt_wasm(password, saltBytes);
  return uint8ArrayToHex(result);
};

// True iff `saltHex` is a usable v3 salt (>= 8 bytes once hex-decoded), i.e.
// what `deriveKeyWithSalt` requires. A genuine v3 account ALWAYS has one; the
// server returns an empty salt as the anti-enumeration default for UNKNOWN
// usernames (`{kdf_version: 3, kdf_salt: ""}`), so callers must use this to tell
// a real v3 login from that default rather than feeding "" to deriveKeyWithSalt
// (which throws) — see loginUser.
export const isValidKdfSalt = (saltHex: string): boolean => {
  try {
    return hexToUint8Array(saltHex).byteLength >= 8;
  } catch {
    return false;
  }
};

// Generate a fresh 16-byte random KDF salt (hex) for a v3 account.
export const generateKdfSalt = (): string =>
  uint8ArrayToHex(crypto.getRandomValues(new Uint8Array(16)));

// ── v2 key schedule (forward-secure credential/KEK separation) ───────────────
// v1 `deriveKey` and `hashPassword` return the SAME bytes, so the credential
// sent to the server is the private-key encryption key. v2 splits the master key
// (`deriveKey` output) with domain-separated HKDF: `kek` wraps the private key
// and stays local; `authSecret` is the server credential, unlinkable from `kek`.
// All return hex, matching `deriveKey`.

// v2 private-key encryption key (use in place of the derived key with
// encryptPrivateKey/decryptPrivateKey for v2 accounts).
export const kek = (password: string, username: string): string =>
  uint8ArrayToHex(kek_wasm(password, username));

// v2 authentication secret — send to the server as the login credential
// (in place of hashPassword). Reveals nothing about `kek`.
export const authSecret = (password: string, username: string): string =>
  uint8ArrayToHex(auth_secret_wasm(password, username));

// v2 KEK / auth secret from an already-derived master key (`deriveKey` hex).
// Lets an already-logged-in device (holding `dk`) upgrade to v2 without
// re-prompting for the password.
export const kekFromMk = (mkHex: string): string =>
  uint8ArrayToHex(kek_from_mk_wasm(hexToUint8Array(mkHex)));

export const authSecretFromMk = (mkHex: string): string =>
  uint8ArrayToHex(auth_secret_from_mk_wasm(hexToUint8Array(mkHex)));

// Decrypt file
// @encryptedFile Uint8Array
// @aesKeyHex hex aes key
// @returns File as Uint8Array
export const decryptAsset = async (
  encryptedFile: Uint8Array,
  aesKeyHex: string,
): Promise<Uint8Array> => {
  const keyUint8Array = hexToUint8Array(aesKeyHex);

  const decryptedArrayBuffer = await windowCryptoDecrypt(
    encryptedFile.buffer,
    keyUint8Array,
  );

  if (!decryptedArrayBuffer) {
    // toast.error(i18n.t("decryptionFailed"));
    // TO DO -> check if this is the correct way to handle this
    // error handling should be done in the middleware?
    return new Uint8Array(); // ??
  }

  return new Uint8Array(decryptedArrayBuffer);
};

// Encrypt file
// @file File
// @aesKeyHex hex aes key
// @returns the encrypted attachment as a Blob (nonce-prefixed AES-GCM output)
//
// Returns the Blob straight from windowCryptoEncrypt rather than flattening it
// to a Uint8Array. This does NOT change the transient encryption peak (the
// plaintext, the one-shot subtle.encrypt output, and the Blob snapshot still
// coexist for ~3x the file size during encryption). What it removes is the
// full-size Uint8Array that was then PINNED on the JS heap for the whole upload
// duration — minutes for a large file. That retained-during-upload copy, summed
// across several concurrent sends, is the memory the tab could not hold. Bytes
// on the wire are identical.
export const encryptAsset = async (
  file: File,
  aesKeyHex: string,
): Promise<Blob> => {
  // Prep data
  const uint8ArrayFile = await fileToUint8Array(file);
  const uint8ArrayKey = hexToUint8Array(aesKeyHex);

  return windowCryptoEncrypt(uint8ArrayFile, uint8ArrayKey);
};
